import { sortingOrder } from 'utils/sort';

import {
  compareStrings,
  compareDates,
  compareNumbers,
  comparePrimaryClinicianName,
  compareModulesCompletedThisWeek,
  compareNumberDaySinceLastVisit,
  compareGiftCardVendors,
  compareSelfAssessmentUrges,
  compareCsvSelfAssessmentDrugUse,
  compareCsvSelfAssessmentUrges,
  compareTimezoneDates,
  defaultComparator,
} from './compare';
import { DATE_FORMATS } from './dateTime';

type SortedByColumnAndDirectionType = (firstRow: any, secondRow: any) => number;

export const sortByColumn = (
  firstRow: any,
  secondRow: any,
  columnName: string,
): number => {
  switch (columnName) {
    case 'birth_date':
    case 'birthDate':
      return compareDates(firstRow.birthDate, secondRow.birthDate);
    case 'clinic':
      return compareStrings(firstRow.clinicName, secondRow.clinicName);
    case 'chosen_gift_card_vendor':
    case 'chosenGiftCardVendor':
      return compareStrings(
        firstRow.chosenGiftCardVendor,
        secondRow.chosenGiftCardVendor,
      );
    case 'email':
      return compareStrings(firstRow.email, secondRow.email);
    case 'first_name':
    case 'firstName':
      return compareStrings(firstRow.firstName, secondRow.firstName);
    case 'gift_card_vendors':
    case 'giftCardVendors':
      return compareGiftCardVendors(
        firstRow.giftCardVendors,
        secondRow.giftCardVendors,
      );
    case 'health_care_provider':
    case 'healthCareProvider':
      return comparePrimaryClinicianName(
        firstRow.healthCareProvider ?? firstRow.primaryHealthCareProviders?.[0],
        secondRow.healthCareProvider ??
          secondRow.primaryHealthCareProviders?.[0],
      );
    case 'external_patient_id':
    case 'externalPatientId':
      return compareStrings(firstRow.externalRef, secondRow.externalRef);
    case 'last_name':
    case 'lastName':
      return compareStrings(firstRow.lastName, secondRow.lastName);
    case 'last_opened':
    case 'lastOpened':
      return compareDates(firstRow.lastOpened, secondRow.lastOpened);
    case 'modules_completed_this_week':
    case 'modulesCompletedThisWeek':
      return compareModulesCompletedThisWeek(
        firstRow.modulesCompletedThisWeek,
        secondRow.modulesCompletedThisWeek,
      );
    case 'number_days_since_last_visit':
    case 'numberDaysSinceLastVisit':
      return compareNumberDaySinceLastVisit(
        { value: firstRow.numberDaysSinceLastVisit, program: firstRow.program },
        {
          value: secondRow.numberDaysSinceLastVisit,
          program: secondRow.program,
        },
      );
    case 'rx_start_date':
    case 'rxStartDate': {
      const firstRowData = {
        dateTime: firstRow?.currentAccessRight?.startDate,
        timezone: firstRow?.currentAccessRight?.timeZone,
      };
      const secondRowData = {
        dateTime: secondRow?.currentAccessRight?.startDate,
        timezone: secondRow?.currentAccessRight?.timeZone,
      };

      return compareTimezoneDates(
        firstRowData,
        secondRowData,
        DATE_FORMATS.UTC_DATE,
      );
    }
    case 'rx_created_date':
    case 'rxCreatedDate':
      return compareDates(firstRow.rxCreatedDate, secondRow.rxCreatedDate);
    case 'rx_days_remaining':
    case 'rxDaysRemaining':
      return compareNumbers(
        firstRow?.currentAccessRight?.daysRemaining,
        secondRow?.currentAccessRight?.daysRemaining,
      );
    case 'total_lessons_completed':
    case 'totalLessonsCompleted':
      return compareNumbers(
        firstRow.totalLessonsCompleted,
        secondRow.totalLessonsCompleted,
      );
    case 'rewards_earned_last_7_days':
    case 'rewardsEarnedLast_7Days':
      return compareNumbers(
        firstRow.rewardsEarnedLast7Days,
        secondRow.rewardsEarnedLast7Days,
      );
    case 'rewards_earned_total':
    case 'rewardsEarnedTotal':
      return compareNumbers(
        firstRow.rewardsEarnedTotal,
        secondRow.rewardsEarnedTotal,
      );
    case 'days_since_clinic_visit':
    case 'daysSinceClinicVisit':
      return compareNumbers(
        firstRow.daysSinceClinicVisit,
        secondRow.daysSinceClinicVisit,
      );
    case 'days_since_self_assessment':
    case 'daysSinceSelfAssessment':
      return compareNumbers(
        firstRow.daysSinceSelfAssessment,
        secondRow.daysSinceSelfAssessment,
      );
    case 'self_assessment_drug_use':
    case 'selfAssessmentDrugUse':
      return compareStrings(
        firstRow.selfAssessmentDrugUse,
        secondRow.selfAssessmentDrugUse,
      );
    case 'self_assessment_urges':
    case 'selfAssessmentUrges':
      return compareSelfAssessmentUrges(
        firstRow.selfAssessmentUrges,
        secondRow.selfAssessmentUrges,
      );
    case 'csv_days_since_clinic_visit':
    case 'csvDaysSinceClinicVisit':
      return compareNumbers(
        firstRow.daysSinceClinicVisit,
        secondRow.daysSinceClinicVisit,
      );
    case 'csv_days_since_self_assessment':
    case 'csvDaysSinceSelfAssessment':
      return compareNumbers(
        firstRow.daysSinceSelfAssessment,
        secondRow.daysSinceSelfAssessment,
      );
    case 'csv_self_assessment_drug_use':
    case 'csvSelfAssessmentDrugUse':
      return compareCsvSelfAssessmentDrugUse(
        firstRow.selfAssessmentDrugUse,
        secondRow.selfAssessmentDrugUse,
      );
    case 'csv_self_assessment_urges':
    case 'csvSelfAssessmentUrges':
      return compareCsvSelfAssessmentUrges(firstRow, secondRow);
    case 'rx_state':
    case 'rxState':
      return compareStrings(
        firstRow.formattedRxState,
        secondRow.formattedRxState,
      );
    case 'analytics_id':
    case 'analyticsId':
      return compareStrings(firstRow.analyticsId, secondRow.analyticsId);
    case 'program':
      return compareStrings(firstRow.program, secondRow.program);
    case 'programLabel':
      return compareStrings(firstRow?.currentAccessRight?.program, secondRow?.currentAccessRight?.program);
    case 'treatment_arm':
    case 'treatmentArm':
      return compareStrings(firstRow.treatmentArm, secondRow.treatmentArm);
    case 'primary_id':
    case 'primaryId':
      return compareStrings(firstRow.id, secondRow.id);
    default:
      throw new Error(`Comparing by unexpected column name: ${columnName}`);
  }
};

const sortByAscending = (
  firstRow: any,
  secondRow: any,
  columnName: string,
): number => {
  const order = sortByColumn(firstRow, secondRow, columnName);
  if (order !== 0) return order;
  return defaultComparator(firstRow, secondRow);
};

export const sortByColumnAndDirection = (
  columnName: string,
  direction: string | null,
): SortedByColumnAndDirectionType => {
  return (firstRow: any, secondRow: any): number => {
    let order = sortByAscending(firstRow, secondRow, columnName);
    return direction === sortingOrder.ASC ? order : -order;
  };
};

export const sorting = (
  columnName: string | null,
  ascending: string | null,
): SortedByColumnAndDirectionType => {
  if (columnName) {
    return sortByColumnAndDirection(columnName, ascending);
  }
  return defaultComparator;
};
